const REFRESH_TOKEN_KEY = 'refresh_token';

/**
 * THE REFRESH TOKEN IS OWNER-BOUND (theme-customization A3, security gate).
 *
 * `localStorage` is shared by every tab on the origin, and this key is a bare browser global:
 * there is no `storage` listener anywhere in `apps/web/src` (verified by search in the edit that
 * wrote this), so a tab has no way to learn that another tab replaced the value. Tab 1 signed in
 * as A, tab 2 signs in as B, tab 1's access token expires: tab 1's in-tab ownership checks all
 * pass (its own generation never moved), it reads whatever the key now holds — B's token —
 * refreshes with it and stores the result as A's session. The tab then renders A's identity
 * while every request it makes is authorized as B. No authorization bypass and no attacker
 * trigger, but a real identity confusion.
 *
 * The remedy is to store WHO the token belongs to beside it and refuse a read that does not
 * match the live user. That closes it at the one read site and the one write site, and unlike a
 * `storage` listener it introduces no cross-tab timing of its own.
 *
 * MIGRATION: a value written before this change is a BARE STRING with no owner. It is treated as
 * UNOWNED and discarded on first read — never trusted — so the worst case for a real browser
 * already holding one is a single re-login, not a silent mis-attribution.
 */
type OwnedRefreshToken = { userId: string; token: string };

function readOwnedRefreshToken(): OwnedRefreshToken | null {
  const raw = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as OwnedRefreshToken).userId === 'string' &&
      typeof (parsed as OwnedRefreshToken).token === 'string'
    ) {
      return parsed as OwnedRefreshToken;
    }
  } catch {
    // A legacy bare string is not JSON. Fall through to the discard below.
  }
  // Legacy or corrupt: unowned, therefore untrusted.
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  return null;
}

/**
 * The stored refresh token, but only if it belongs to `userId`. A token stored by a DIFFERENT
 * account — another tab's sign-in — reads as absent.
 */
export function getRefreshToken(userId: string | null): string | null {
  if (!userId) return null;
  const owned = readOwnedRefreshToken();
  if (owned === null || owned.userId !== userId) return null;
  return owned.token;
}

export function setRefreshToken(token: string, userId: string): void {
  localStorage.setItem(REFRESH_TOKEN_KEY, JSON.stringify({ userId, token }));
}

/**
 * Remove the stored token — but only if it belongs to `userId`.
 *
 * Found by the cross-tab guard in `app/__tests__/ThemeProvider.test.tsx`: an unconditional
 * `removeItem` meant tab 1 signing out (or failing its own refresh) deleted the token tab 2 had
 * just stored for a DIFFERENT account, silently ending a session nobody asked to end. Passing
 * no owner keeps the unconditional behaviour, which is what a corrupt/unowned value deserves.
 */
export function clearRefreshToken(userId?: string | null): void {
  if (userId !== undefined && userId !== null) {
    const owned = readOwnedRefreshToken();
    if (owned !== null && owned.userId !== userId) return;
  }
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

// ─── Authentication attempts (theme-customization A3) ────────────────────────

/**
 * The AUTH-ATTEMPT EPOCH: an in-memory counter identifying "which sign-in attempt is current".
 *
 * A password login and a magic-link consume are both "POST, then commit tokens + session +
 * cleared cache + navigation". Between the POST and its response the user can start ANOTHER
 * attempt (a second submit, a different account) or sign out. Without this counter the older
 * response wins whatever order it arrives in, and it wins with the OTHER account's credentials
 * already in place — so a stale success could write a refresh token for an account nobody is
 * signed into.
 *
 * Allocate before the request (`beginAuthAttempt`), then gate every commit on
 * `isCurrentAuthAttempt(id)`. A stale success writes NOTHING — not even its refresh token.
 *
 * Deliberately module state rather than store state: it is never rendered, and a Zustand update
 * per attempt would re-render every auth consumer for a value only these callbacks read.
 */
let authAttempt = 0;

/** Allocate the epoch for an attempt that is about to start. Invalidates every older attempt. */
export function beginAuthAttempt(): number {
  authAttempt += 1;
  return authAttempt;
}

export function isCurrentAuthAttempt(attempt: number): boolean {
  return attempt === authAttempt;
}

/** Logout start: no in-flight attempt may commit after this. */
export function invalidateAuthAttempts(): void {
  authAttempt += 1;
}

// ─── Token refresh (theme-customization A3: generation-owned) ────────────────

/**
 * Who owns the session a request was issued under.
 *
 * `owns()` is captured by the CALLER at request start (`lib/api.ts` closes over the session
 * generation it read before the fetch) and evaluated HERE at the two moments that matter.
 * The predicate is injected rather than read from the store directly so this module keeps no
 * dependency on `stores/authStore.ts` — that module already imports `clearRefreshToken` from
 * here, and an import cycle between the token store and the session store is exactly the kind
 * of load-order fragility this batch should not add.
 */
export interface RefreshOwnership {
  owns: () => boolean;
  /** The account the originating request was issued for; the stored token must belong to it. */
  userId: string | null;
}

/**
 * Attempt to refresh the access token using the stored refresh token.
 * Returns the new access token on success, or null on failure.
 * The caller is responsible for updating the Zustand auth store with the new token.
 *
 * THREE CHECKS, and they are not the same check three times:
 *
 *   1. IN-TAB OWNERSHIP, BEFORE ISSUING THE REQUEST. A 401 handler belonging to a session that
 *      is no longer current in THIS tab must not refresh at all.
 *   2. TOKEN OWNERSHIP, at the read. `getRefreshToken(userId)` returns the stored token only if
 *      it belongs to the account the request was issued for.
 *
 *      CORRECTED at the A3 security gate. This comment previously said the read "happens only
 *      while the originating generation is still the active one, so the value read is that
 *      session's". ACROSS TABS THAT WAS FALSE: the generation is per-tab in-memory state and
 *      `localStorage` is per-origin, so another tab's sign-in replaced the value without moving
 *      this tab's generation at all. The owner binding above is what makes the sentence true now,
 *      and it is true because of the stored `userId`, not because of the generation.
 *   3. BEFORE PERSISTING THE ROTATED TOKEN. The round-trip is where the account can change, and
 *      a rotated token written after that point would replace the new session's credential with
 *      one issued for the old account. On a failed check the rotated token is simply dropped:
 *      the server has rotated it, but nobody is signed in with it, and the current session's own
 *      token is untouched.
 *
 * A same-session refresh still uses its LATEST rotated token — the read is of live storage, and
 * a rotation by this same account writes back under the same owner.
 *
 * `ownership` is optional so a caller with no session context (and every existing test double)
 * keeps the previous unconditional behaviour; without it no token can be read, because a refresh
 * with no named owner is exactly the confusion this parameter exists to prevent.
 */
export async function refreshAccessToken(ownership?: RefreshOwnership): Promise<string | null> {
  if (ownership && !ownership.owns()) return null;

  const refreshToken = getRefreshToken(ownership?.userId ?? null);
  if (!refreshToken) return null;

  const apiBase = import.meta.env.VITE_API_URL ?? '';

  try {
    const response = await fetch(`${apiBase}/api/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) return null;

    const result = await response.json();
    const { accessToken, refreshToken: newRefreshToken } = result.data;
    if (ownership && !ownership.owns()) return null;
    if (ownership?.userId) setRefreshToken(newRefreshToken, ownership.userId);
    return accessToken;
  } catch {
    return null;
  }
}
